import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router, RouterStateSnapshot } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import {
  getLocalStorageRoleNames,
  isTeamManager,
  isTeamManagerInAnyAccount,
  SelfServeLocalStorageUtil,
} from '../utils/local-storage.util';
import { FeatureFlagsService } from '../services/feature-flags.service';
import { PersonalizedPracticeService } from '../services/personalized-practice/personalized-practice.service';
import { AccountFeatureService } from '../services/account-feature/account-feature.service';
import { AppConstants } from '../app-constants';
import { NativeCallService } from '../services/native-call.service';

export const roleGuard: CanActivateFn = async (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
  const router = inject(Router);
  const featureFlagsService = inject(FeatureFlagsService);
  const personalizedPracticeService = inject(PersonalizedPracticeService);
  const accountFeatureService = inject(AccountFeatureService);
  const nativeCallService = inject(NativeCallService);
  const url: string = state.url;
  const userRoles: string[] = getLocalStorageRoleNames();
  const hasSimulationBuilderAccess: boolean = SelfServeLocalStorageUtil.hasSimulationBuilderAccess();
  const homeUrls: string[] = ['', '/', '/home', '/team_manager', '/team_manager/home'];
  const teamAdminUrls: string[] = ['/team_manager', '/team_manager/home', '/team_manager/my_teams'];
  const simulationBuilderUrl: string[] = ['/self-serve'];
  const reportingUrl: string[] = ['/reporting_dashboard'];
  const leaderboardUrl: string[] = ['/leaderboard'];
  const personalizedPracticeUrl: string[] = ['/personalized-practice'];
  const learningPathwayUrl: string[] = ['/learning-pathway'];
  const isTeamAdmin = isTeamManager();
  const isTeamAdminInAnyAccount = isTeamManagerInAnyAccount();
  const isLearner: boolean = userRoles.includes("Learner");
  const enablementAdmin: boolean = userRoles.includes("EnablementAdmin");

  const isLeaderboardEnabled = await accountFeatureService.isFeatureActive(AppConstants.ACCOUNT_FEATURE_LEADERBOARD_TAB_VISIBILITY);
  const isMyTeamsEnabled = await featureFlagsService.isFlagEnabled(AppConstants.MY_TEAMS_UI);
  const isReportingUrlEnabled = await featureFlagsService.isFlagEnabled(AppConstants.NEW_REPORTING_UI);
  const isPersonalizedPracticeEnabled = await featureFlagsService.isFlagEnabled(AppConstants.PERSONALISED_PRACTICE_UI);
  const hasPersonalizedPracticeProducts = await firstValueFrom(
    personalizedPracticeService.hasPersonalizedPracticeProducts$
  );
  const isSimulationReportViewEnabled = await featureFlagsService.isFlagEnabled(AppConstants.SIMULATION_REPORT_VIEW);
  const isLearningPathwaysEnabled = await featureFlagsService.isFlagEnabled('Learning_Pathways');
  const isHideIndividualUserDataEnabled = await accountFeatureService.isFeatureActive(AppConstants.ACCOUNT_FEATURE_HIDE_INDIVIDUAL_USER_DATA);

  if ((simulationBuilderUrl.includes(url) && !hasSimulationBuilderAccess)) {
    return router.parseUrl('/');
  }

  // Legacy Reporting V1 routes were removed: send old links to the reporting
  // dashboard when accessible, otherwise fall back to assignments.
  if (['/reporting', '/reporting_visualization'].includes(url.split('?')[0])) {
    const hasDashboardAccess = (isTeamAdminInAnyAccount || enablementAdmin) && isReportingUrlEnabled;
    return router.parseUrl(hasDashboardAccess ? '/reporting_dashboard' : '/assignments');
  }


  if (isTeamAdmin && isMyTeamsEnabled && homeUrls.includes(url)) {
    return router.parseUrl('/team_manager/my_teams');
  }


  if (!isTeamAdmin && isLearner && homeUrls.includes(url)) {
    return router.parseUrl('/assignments');
  }

  if (!isTeamAdmin && !isLearner && !enablementAdmin && hasSimulationBuilderAccess && homeUrls.includes(url)) {
    return router.parseUrl('/self-serve');
  }
  
  // Redirect to personalized practice only if learner, feature flag enabled, and products available
  if (!isTeamAdmin && isLearner && isPersonalizedPracticeEnabled && hasPersonalizedPracticeProducts && homeUrls.includes(url)) {
    return router.parseUrl('/personalized-practice');
  }

  if (isTeamAdmin && !isMyTeamsEnabled && isLearner && homeUrls.includes(url)) {
    return router.parseUrl('/assignments');
  }

  // On native iOS, skip the leaderboard home-redirect — non-learners fall through to HomeComponent's no-role-card
  if (isLeaderboardEnabled && (isLearner || enablementAdmin || isTeamAdmin) && homeUrls.includes(url) && !nativeCallService.isNativeIos) {
    return router.parseUrl('/leaderboard');
  }

  // Managers of teams only in a secondary account have no other home page; skipped on native iOS,
  // where nativeIosGuard blocks the dashboard.
  if (isTeamAdminInAnyAccount && isReportingUrlEnabled && homeUrls.includes(url) && !nativeCallService.isNativeIos) {
    return router.parseUrl('/reporting_dashboard');
  }

  if (!isTeamAdmin && teamAdminUrls.includes(url)) {
    return router.parseUrl('/');
  }

  if (!isTeamAdmin && !isLearner && ['/assignments', '/team_manager/assignments', '/team_manager/my_teams'].includes(url)) {
    return router.parseUrl('/');
  }

  // Covers the dashboard detail pages (/reporting_dashboard/user/:id, ...) and query strings too.
  const isReportingDashboardUrl = reportingUrl.some((reportingRoute) => url.split('?')[0].startsWith(reportingRoute));
  if ((!isTeamAdminInAnyAccount && !enablementAdmin || !isReportingUrlEnabled) && isReportingDashboardUrl) {
    return router.parseUrl('/');
  }

  if (!isLeaderboardEnabled && leaderboardUrl.includes(url)) {
    return router.parseUrl('/');
  }

  // Block direct access to learning-pathway when Learning_Pathways flag is off
  if (!isLearningPathwaysEnabled && learningPathwayUrl.includes(state.url.split('?')[0])) {
    return router.parseUrl('/assignments');
  }

  if ((!isLearner || !isPersonalizedPracticeEnabled || !hasPersonalizedPracticeProducts) && personalizedPracticeUrl.includes(url)) {
    return router.parseUrl('/');
  }

  if (!isMyTeamsEnabled && url.includes('/team_manager/my_teams')) {
    return router.parseUrl('/');
  }

  if (!isSimulationReportViewEnabled && url.includes('/reporting_dashboard/simulation/')) {
    return router.parseUrl('/reporting_dashboard');
  }

  if (isHideIndividualUserDataEnabled && url.includes('/reporting_dashboard/user/')) {
    return router.parseUrl('/reporting_dashboard');
  }

  return true;

};
